import { Link, useNavigate } from "@tanstack/react-router";
import { format, parseISO } from "date-fns";
import { ArrowLeft, Check, Pencil, Send, ShoppingCart, XCircle } from "lucide-react";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorBoundary } from "@/components/shared/error-boundary";
import { StatusDot } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatMoney, labelFor, PAYMENT_TERMS } from "@/features/clients/constants";
import { useQuotation, useUpdateQuotationStatus } from "@/features/quotations/api";
import { useConvertQuotationToSalesOrder } from "@/features/sales-orders/api";
import {
  QT_STATUSES,
  canEditLines,
  labelFor as qtLabelFor,
  statusTone,
} from "@/features/quotations/constants";
import type { QuotationStatus } from "@/features/quotations/types";

export function QuotationDetailPage({ qtId }: { qtId: string }) {
  return (
    <ErrorBoundary title="Quotation detail crashed">
      <QuotationDetailInner qtId={qtId} />
    </ErrorBoundary>
  );
}

function QuotationDetailInner({ qtId }: { qtId: string }) {
  const navigate = useNavigate();
  const { data: qt, isLoading, error } = useQuotation(qtId);
  const updateStatus = useUpdateQuotationStatus();
  const convert = useConvertQuotationToSalesOrder();

  if (isLoading) {
    return <p className="py-16 text-center text-sm text-muted-foreground">Loading…</p>;
  }

  if (error || !qt) {
    return (
      <EmptyState
        title="Quotation not found"
        description={(error as Error)?.message ?? "Unknown quotation"}
        action={
          <Button asChild variant="outline" size="sm">
            <Link to="/quotations">Back to list</Link>
          </Button>
        }
      />
    );
  }

  async function setStatus(status: QuotationStatus) {
    await updateStatus.mutateAsync({ id: qtId, status });
  }

  return (
    <div className="mx-auto max-w-4xl space-y-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-2">
          <Button asChild variant="ghost" size="icon-sm">
            <Link to="/quotations">
              <ArrowLeft />
            </Link>
          </Button>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="font-mono text-lg font-semibold">{qt.qt_number}</h1>
              <StatusDot tone={statusTone(qt.status)}>
                {qtLabelFor(QT_STATUSES, qt.status)}
              </StatusDot>
            </div>
            <p className="text-xs text-muted-foreground">
              {qt.client_code} · {qt.client_name} · {format(parseISO(qt.issue_date), "dd MMM yyyy")}
              {qt.valid_until ? ` · valid until ${format(parseISO(qt.valid_until), "dd MMM yyyy")}` : ""}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {canEditLines(qt.status) || qt.status === "sent" ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => navigate({ to: "/quotations/$qtId/edit", params: { qtId } })}
            >
              <Pencil />
              Edit
            </Button>
          ) : null}
          {qt.status === "draft" ? (
            <Button
              type="button"
              size="sm"
              disabled={updateStatus.isPending}
              onClick={() => setStatus("sent")}
            >
              <Send />
              Mark sent
            </Button>
          ) : null}
          {qt.status === "sent" ? (
            <>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={updateStatus.isPending}
                onClick={() => setStatus("accepted")}
              >
                <Check />
                Accept
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={updateStatus.isPending}
                onClick={() => setStatus("rejected")}
              >
                <XCircle />
                Reject
              </Button>
            </>
          ) : null}
          {["draft", "sent"].includes(qt.status) ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={updateStatus.isPending}
              onClick={() => setStatus("cancelled")}
            >
              Cancel
            </Button>
          ) : null}
        </div>
      </header>

      <div className="grid gap-3 text-xs md:grid-cols-2">
        <section className="rounded border p-3">
          <h2 className="mb-1 font-medium">Bill to</h2>
          <pre className="whitespace-pre-wrap font-sans text-muted-foreground">
            {qt.bill_to_text || "—"}
          </pre>
        </section>
        <section className="rounded border p-3">
          <h2 className="mb-1 font-medium">Commercial</h2>
          <dl className="space-y-1 text-muted-foreground">
            <div className="flex justify-between gap-2">
              <dt>Payment</dt>
              <dd>{labelFor(PAYMENT_TERMS, qt.payment_terms)}</dd>
            </div>
            <div className="flex justify-between gap-2">
              <dt>Ship to</dt>
              <dd className="max-w-[60%] text-right">{qt.ship_to_text || "—"}</dd>
            </div>
          </dl>
        </section>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>#</TableHead>
              <TableHead>Material</TableHead>
              <TableHead>Grade</TableHead>
              <TableHead className="text-right">Qty</TableHead>
              <TableHead className="text-right">Wt</TableHead>
              <TableHead className="text-right">Rate</TableHead>
              <TableHead className="text-right">GST</TableHead>
              <TableHead className="text-right">Amount</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(qt.lines ?? []).map((line) => (
              <TableRow key={line.id} className="text-xs">
                <TableCell>{line.line_no}</TableCell>
                <TableCell>
                  {line.material_name}
                  {line.shape_label ? ` · ${line.shape_label}` : ""}
                  {line.description ? (
                    <span className="block text-2xs text-muted-foreground">{line.description}</span>
                  ) : null}
                </TableCell>
                <TableCell>{line.grade_name ?? "—"}</TableCell>
                <TableCell className="text-right tabular-nums">{line.qty || "—"}</TableCell>
                <TableCell className="text-right tabular-nums">
                  {line.weight > 0 ? line.weight : "—"}
                </TableCell>
                <TableCell className="text-right tabular-nums">{line.unit_rate}</TableCell>
                <TableCell className="text-right tabular-nums">{line.gst_slab}%</TableCell>
                <TableCell className="text-right tabular-nums font-medium">
                  {formatMoney(line.line_subtotal)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <footer className="flex flex-wrap justify-end gap-4 text-xs">
        <div className="space-y-1 text-right">
          <div>
            Subtotal <strong className="tabular-nums">{formatMoney(qt.subtotal)}</strong>
          </div>
          {qt.discount > 0 ? (
            <div>
              Discount <strong className="tabular-nums">−{formatMoney(qt.discount)}</strong>
            </div>
          ) : null}
          <div>
            Tax <strong className="tabular-nums">{formatMoney(qt.tax_total)}</strong>
          </div>
          {qt.adjustment !== 0 ? (
            <div>
              Adjustment <strong className="tabular-nums">{formatMoney(qt.adjustment)}</strong>
            </div>
          ) : null}
          <div className="text-sm">
            Grand total{" "}
            <strong className="tabular-nums text-primary">{formatMoney(qt.grand_total)}</strong>
          </div>
        </div>
      </footer>

      {qt.notes || qt.terms ? (
        <div className="grid gap-3 text-xs md:grid-cols-2">
          {qt.notes ? (
            <section className="rounded border p-3">
              <h2 className="mb-1 font-medium">Notes</h2>
              <p className="whitespace-pre-wrap text-muted-foreground">{qt.notes}</p>
            </section>
          ) : null}
          {qt.terms ? (
            <section className="rounded border p-3">
              <h2 className="mb-1 font-medium">Terms</h2>
              <p className="whitespace-pre-wrap text-muted-foreground">{qt.terms}</p>
            </section>
          ) : null}
        </div>
      ) : null}

      {qt.status === "accepted" ? (
        <div className="flex flex-wrap items-center gap-2 rounded border border-dashed p-3 text-xs">
          <span className="text-muted-foreground">Ready to convert to a confirmed sales order.</span>
          <Button
            type="button"
            size="sm"
            disabled={convert.isPending}
            onClick={async () => {
              const created = await convert.mutateAsync(qtId);
              navigate({ to: "/sales-orders/$soId", params: { soId: created.id } });
            }}
          >
            <ShoppingCart />
            Convert to SO
          </Button>
        </div>
      ) : null}
      {qt.status === "converted" && qt.sales_order_id ? (
        <Button asChild variant="outline" size="sm">
          <Link to="/sales-orders/$soId" params={{ soId: qt.sales_order_id }}>
            Open sales order
          </Link>
        </Button>
      ) : null}
    </div>
  );
}
